import type {
  SegmentedControlActiveStyle,
  SegmentedControlSize,
} from './SegmentedControl.types';

/**
 * Icon size per control size — applies to leading icons and icon-only
 * segments. From the assembled control (Figma 1528:1833) per-size icon dims.
 */
export const ICON_SIZE_FOR: Record<SegmentedControlSize, number> = {
  xxs: 16,
  xs: 16,
  s: 18,
  m: 20,
  l: 22,
  xl: 24,
  xxl: 26,
};

/**
 * Resolved icon colour per (active, activeStyle, dark-mode). Mirrors the
 * segment text colours. Only consulted on NATIVE — on web the icon inherits
 * `currentColor` from the segment's CSS text colour (see `SegmentedControl`).
 *
 * Dark values follow the refreshed DARK Segment set (4455:12257, read
 * 2026-07-14) — plain auto-inverting tokens: primary style is constant
 * #f5f3ff (light primary-50 / dark primary-950), secondary/tertiary are
 * primary-700 (#7008e7 light / #c4b4ff dark). Keep in sync with the active
 * compounds in `segmentVariants` / `segmentLabelColorVariants`.
 */
const INACTIVE = { light: '#737373', dark: '#737373' }; // secondary-500 (same both modes)
const ACTIVE: Record<SegmentedControlActiveStyle, { light: string; dark: string }> = {
  // primary-50 light · primary-950 dark — both resolve #f5f3ff
  primary: { light: '#f5f3ff', dark: '#f5f3ff' },
  // primary-700 — auto-inverts to #c4b4ff in dark
  secondary: { light: '#7008e7', dark: '#c4b4ff' },
  // secondary-800 — neutral, auto-inverts to #e5e5e5 (secondary-200) in dark.
  tertiary: { light: '#262626', dark: '#e5e5e5' },
};

export function resolveSegmentIconColor(
  active: boolean,
  activeStyle: SegmentedControlActiveStyle,
  isDark: boolean,
): string {
  const pair = active ? ACTIVE[activeStyle] : INACTIVE;
  return isDark ? pair.dark : pair.light;
}
